import React, { useCallback } from 'react';
import { FlatList, StyleSheet, TouchableOpacity } from 'react-native';

import { ChevronRightRounded } from '@/assets/icons/chevronRightRounded';
import { LoadingSkeleton } from '@/components/SkeletonLoader';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { designSystem } from '@/styles/styles';

import { RipsCategory, RipsPack } from '../hooks/use-rips-games';
import { RIPS_ROW_PADDING, useRipsRowItemWidth } from '../hooks/use-rips-row-item-width';
import { RIPS_PACK_CARD_RADIUS, RIPS_PACK_TILE_ASPECT_RATIO, RipsPackCard } from './RipsPackCard';

const CARD_GAP = 12;
const HEADER_LINE_HEIGHT = 24;
const SKELETON_HEADER_WIDTH = 120;
const SKELETON_CARDS = 3;

const keyExtractor = (pack: RipsPack) => pack.vendorId;

type RipsPackSectionProps = {
    category: RipsCategory;
    onCategoryPress: (category: RipsCategory) => void;
    onPackPress: (pack: RipsPack) => void;
};

const RipsPackSection = ({ category, onCategoryPress, onPackPress }: RipsPackSectionProps) => {
    const cardWidth = useRipsRowItemWidth(CARD_GAP);

    const renderItem = useCallback(
        ({ item }: { item: RipsPack }) => <RipsPackCard pack={item} width={cardWidth} onPress={onPackPress} />,
        [cardWidth, onPackPress]
    );

    return (
        <Box pb="s32">
            <TouchableOpacity
                onPress={() => onCategoryPress(category)}
                activeOpacity={0.8}
                hitSlop={8}
                style={styles.header}
                testID={`ripsPackSectionHeader-${category.gameId}`}
            >
                <Row alignItems="center" gap="s4">
                    <Text variant="headlineSmall" lineHeight={HEADER_LINE_HEIGHT} color={ds.colorToken.text.primary}>
                        {category.name}
                    </Text>
                    <ChevronRightRounded />
                </Row>
            </TouchableOpacity>
            <FlatList
                data={category.packs}
                keyExtractor={keyExtractor}
                renderItem={renderItem}
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.list}
            />
        </Box>
    );
};

/** One placeholder section while loading, since the number of sections isn't known yet. */
const RipsPackSectionSkeleton = () => {
    const cardWidth = useRipsRowItemWidth(CARD_GAP);

    return (
        <Box pb="s32" testID="ripsPackSectionSkeleton">
            <Box style={styles.header}>
                <LoadingSkeleton
                    width={SKELETON_HEADER_WIDTH}
                    height={HEADER_LINE_HEIGHT}
                    borderRadius="r8"
                    backgroundColor={ds.colorToken.background.neutral.subtler}
                    skeletonColor={designSystem.colors.neutral300}
                />
            </Box>
            <Row style={[styles.list, styles.skeletonRow]}>
                {[...Array(SKELETON_CARDS)].map((_, index) => (
                    <LoadingSkeleton
                        key={index}
                        width={cardWidth}
                        height={Math.round(cardWidth / RIPS_PACK_TILE_ASPECT_RATIO)}
                        style={styles.skeletonCard}
                        backgroundColor={ds.colorToken.background.neutral.subtler}
                        skeletonColor={designSystem.colors.neutral300}
                    />
                ))}
            </Row>
        </Box>
    );
};

type RipsPackSectionsProps = {
    categories: RipsCategory[];
    isLoading?: boolean;
    onCategoryPress: (category: RipsCategory) => void;
    onPackPress: (pack: RipsPack) => void;
};

/**
 * One section per category that has packs: "<Category> >" header (opens the category) and a horizontal row of packs.
 */
export const RipsPackSections = ({ categories, isLoading, onCategoryPress, onPackPress }: RipsPackSectionsProps) => {
    if (isLoading) {
        return <RipsPackSectionSkeleton />;
    }

    return (
        <>
            {categories
                .filter(category => category.packs.length > 0)
                .map(category => (
                    <RipsPackSection
                        key={category.gameId}
                        category={category}
                        onCategoryPress={onCategoryPress}
                        onPackPress={onPackPress}
                    />
                ))}
        </>
    );
};

const styles = StyleSheet.create({
    header: {
        alignSelf: 'flex-start',
        marginHorizontal: RIPS_ROW_PADDING,
        marginBottom: 16,
    },
    list: {
        paddingHorizontal: RIPS_ROW_PADDING,
        gap: CARD_GAP,
    },
    skeletonRow: {
        overflow: 'hidden',
    },
    skeletonCard: {
        borderRadius: RIPS_PACK_CARD_RADIUS,
    },
});
